import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import withStyles from 'react-jss';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getAuthState, getTenantState } from '../../store/selectors';
import { setRegisterOptions, setRogueTenantSSO } from '../../store/actions';
import TenantFederation from '../auth/TenantFederation';
import RegisterIcon from '../auth/RegisterIcon.jsx';
import {
  handleFederatedSignIn,
  getSocialIcon,
  socialLoginIcons,
} from '../auth/authUtil';
import emailIcon from '../../ASUPhotos/icons/me3_SSO_Envelope.svg';
import humanIcon from '../../ASUPhotos/icons/me3_SSO_Register.svg';
import { getFederationName } from '../auth/federationNameStrings';

const registrationStyles = {
  color: (props) => ({
    backgroundColor: props.tenant.secondary,
    color: 'black',

    '&:hover': {
      backgroundColor: 'white',
      border: `2px solid ${props.tenant.secondary}`,
      color: 'black',
      borderStyle: 'solid',
    },
  }),
};

function RegisterOptions(props) {
  const { t } = useTranslation();
  const registerWithEmailStyle =
    'register-options-button register-with-email-button';
  const registerWithTenantStyle =
    'register-options-button register-with-tenant-button';

  const tenantName =
    props.tenant && props.tenant.name && props.tenant.name.toUpperCase();

  const registerWithEmailText = t('Register with Email');
  const loginWithText = t('Login Now');

  const federationName = getFederationName(tenantName);
  const registrationName =
    tenantName === 'ASU'
      ? t('my {{account}}', { account: federationName })
      : federationName;
  const registerWithTenantText = t('Register with {{registrationName}}', {
    registrationName,
  });

  const getRegisterWithEmail = () => {
    props.setRegisterOptions('email');
    props.setRogueTenantSSO(false);
  };

  const handleSocialRegister = async (loginProvider) => {
    await props.handleFederatedSignIn(loginProvider);
  };

  const registerSocialIconClass = 'register-social-icon';

  const getRegisterWithSocial = () => {
    const iconList = socialLoginIcons.map((eachIcon) => {
      return getSocialIcon(
        handleSocialRegister,
        eachIcon,
        registerSocialIconClass
      );
    });

    return (
      <div className="social-registration-list">
        {' '}
        <h2 className="register-options-text">{t('Register With Social')}</h2>
        <div className="social-icon-list">{iconList}</div>
      </div>
    );
  };

  const onFederatedLoginClick = (providerName) => {
    if (providerName) props.handleFederatedSignIn(providerName);
  };

  const onLoginClick = (providerName) => {
    const loginRoute = `/${providerName}/login`;
    props.history.push(loginRoute);
  };

  const getRegisterWithTenantCredentials = () => {
    return (
      <TenantFederation
        onFederatedLoginClick={onFederatedLoginClick(tenantName)}
        provider={tenantName}
        buttonText={t('Login with {{signOnProvider}}', { signOnProvider: '' })}
        primaryBackground={props.classes.secondaryBackground}
      />
    );
  };

  const getLoginWithCredentials = () => {
    return (
      <TenantFederation
        onFederatedLoginClick={onLoginClick(tenantName)}
        provider={tenantName}
        buttonText={loginWithText}
        primaryBackground={props.classes.secondaryBackground}
      />
    );
  };

  const getLogInButton = (
    additionalClassName,
    backgroundColor,
    onClickHandler,
    buttonText,
    logInIcon
  ) => {
    return (
      <button
        className={classNames(
          'button--primary',
          'login-button',
          [additionalClassName],
          [props.classes.color],
          [backgroundColor],
          'registration-option-buttons'
        )}
        style={{ borderColor: props.tenant.secondary }}
        type="submit"
        onClick={() => {
          onClickHandler();
        }}
      >
        <RegisterIcon
          iconImage={logInIcon}
          ariaLabelText="icon for Login"
          propsClassName="--options"
          noRole
        />
        {buttonText}
      </button>
    );
  };

  const getRegisterButton = (
    additionalClassName,
    backgroundColor,
    onClickHandler,
    buttonText,
    registerIcon
  ) => {
    return (
      <button
        className={classNames(
          'button--primary',
          'login-button',
          [additionalClassName],
          [props.classes.color],
          [backgroundColor],
          'registration-option-buttons'
        )}
        style={{ borderColor: props.tenant.secondary }}
        data-cy={
          buttonText.toLowerCase().includes('email')
            ? 'register-with-email'
            : 'register-with-federation'
        }
        type="submit"
        onClick={() => {
          onClickHandler();
        }}
      >
        <RegisterIcon
          iconImage={registerIcon}
          ariaLabelText="icon for different types of registration"
          propsClassName="--options"
          noRole
        />
        {buttonText}
      </button>
    );
  };

  const getRegisterOptions = () => {
    return (
      <div
        className={classNames('main-right-content', 'register-options-content')}
      >
        <h2 className="register-options-text">
          {t('How Would You Like To Register')}
        </h2>
        {getRegisterButton(
          registerWithEmailStyle,
          props.classes.secondaryBackground,
          getRegisterWithEmail,
          registerWithEmailText,
          emailIcon
        )}
        {props.tenantHasSSO &&
          getRegisterButton(
            registerWithTenantStyle,
            props.classes.secondaryBackground,
            getRegisterWithTenantCredentials,
            registerWithTenantText,
            humanIcon
          )}
        {getRegisterWithSocial()}
        <h2 className="already-options-text">
          {t('Do You Already Have an Account')}
        </h2>
        {getLogInButton(
          registerWithTenantStyle,
          props.classes.secondaryBackground,
          getLoginWithCredentials,
          loginWithText,
          humanIcon
        )}
      </div>
    );
  };
  return getRegisterOptions();
}

const mapStateToProps = (state) => {
  const tenant = getTenantState(state);
  const auth = getAuthState(state);
  return { tenant, auth };
};

const mapDispatchToProps = {
  handleFederatedSignIn,
  setRegisterOptions,
  setRogueTenantSSO,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withStyles(registrationStyles)(RegisterOptions))
);
